import { ApolloProvider, useApolloClient } from '@apollo/react-hooks'
import type { OfflineQuotesQuery, AppDocsQuery } from '@paintscout/api'
import {
  AppDocsDocument,
  OfflineQuotesDocument,
  updateCachedContact,
  updateCachedQuote,
  useAppDocsQuery
} from '@paintscout/api'
import { getHighestRole } from '@paintscout/util/users'
import * as Sentry from '@sentry/react'
import { createLogger } from '@paintscout/logger'
import { SnackbarProvider, TranslationProvider } from '@paintscout/ui'
import { DevPermissionsProvider, DevUserProvider, Theme, UserProvider } from '@paintscout/ui-business'
import type ApolloCache from '@wora/apollo-cache'
import type ApolloClient from 'apollo-client'
import { error as analyticsError } from 'shared/util/analytics'
import type { Auth0Session } from 'react-auth0'
import type { QuoteDocument } from 'paintscout'
import React, { startTransition, useEffect, useState } from 'react'
import { Authorizer, useAuth } from 'react-auth0'
import { useConnection } from 'react-detect-offline'
import { useHistory } from 'react-router-dom'
import { ApolloQueue, createApolloClient } from 'shared/graphql'
import AppLoading from 'src/components/App/AppLoading'
import { getAuthToken } from 'shared/graphql/client'
import { getCacheScopeKey, MASQUERADE_COMPANY_ID_KEY, resolveMasqueradeCompanyId } from 'shared/graphql/masquerade'
import LaunchDarkly from 'shared/components/common/LaunchDarkly'
import ClientOptionsProvider from './ClientOptionsProvider'
import { buildUserForProvider } from './buildUserForProvider'

const log = createLogger('authorize-user')

const UNEXPECTED_ERROR_MESSAGE = 'An unexpected error occurred'

/**
 * Rejects if `promise` doesn't settle within `ms`. Used to guard the IndexedDB calls during
 * startup: WebKit's IndexedDB is known to hang on `open()` (never firing success/error) after an
 * iOS update until the origin's website data is cleared. Without a timeout the app would sit on
 * the loading spinner forever; with it we fall back to online-only mode and still render.
 */
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_resolve, reject) => setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms))
  ])
}

export default function AuthorizeUser({ children }: { children?: React.ReactNode }) {
  const { session } = useAuth()

  const history = useHistory()
  const isPainterAccount = session?.app_metadata?.roles?.includes('painter')
  const isSuperadmin = session?.app_metadata?.roles?.includes('superadmin')
  const isSupport = session?.app_metadata?.roles?.includes('support')
  const isCorporate = session?.app_metadata?.roles?.includes('corporate')
  const [client, setClient] = useState<ApolloClient<any>>(null)
  const canMasquerade = !!(isSuperadmin || isSupport || isCorporate)
  const locationSearch = history.location.search

  const [masqueradeCompanyId, setMasqueradeCompanyId] = useState(() =>
    resolveMasqueradeCompanyId({ canMasquerade, search: locationSearch })
  )

  // Scopes every company-scoped cache to (user, masqueraded company). Bare user_id when not
  // masquerading, so normal users keep their existing offline store.
  const cacheScope = getCacheScopeKey(session?.user_id, masqueradeCompanyId)

  // `search` matters as much as `pathname` here: navigating /dashboard -> /dashboard?masqueradeCompanyId=X
  // leaves the pathname untouched, so keying only on pathname would ignore the company switch entirely.
  useEffect(() => {
    setMasqueradeCompanyId(resolveMasqueradeCompanyId({ canMasquerade, search: locationSearch }))
  }, [history.location.pathname, locationSearch, canMasquerade])

  useEffect(() => {
    async function supportsIdb() {
      await new Promise((resolve, reject) => {
        const db = window.indexedDB.open('test')

        db.onerror = reject

        db.onsuccess = () => {
          window.indexedDB.deleteDatabase('test')
          resolve(null)
        }
      })
    }

    async function init() {
      if (session) {
        let enableOffline = !isPainterAccount
        if (enableOffline) {
          try {
            // check if idb is supported on this browser — time-boxed because WebKit can hang here
            await withTimeout(supportsIdb(), 4000, 'IndexedDB support check')
          } catch (e) {
            enableOffline = false
            console.log(e)
            window.alert(
              'Your browser is unsupported or running in incognito/private mode. If you go offline, you will be unable to save your changes.'
            )
          }
        }

        try {
          const client = createApolloClient({
            // scoped per masqueraded company — a shared store let one company's $options/$rates
            // hydrate into another company's session (ENG-1061)
            idbName: cacheScope,
            enableOffline
          })

          try {
            // time-boxed: a hung IDB never throws, so a plain await here could block render forever
            await withTimeout(client.hydrate(), 5000, 'IDB hydration')
          } catch (hydrateError) {
            // IDB data may be stale, corrupted, or hanging — continue without offline support
            console.warn('IDB hydration failed, falling back to online-only mode:', hydrateError)
            enableOffline = false
          }
          await new Promise((resolve) => setTimeout(resolve))
          setClient(client)

          try {
            await getAuthToken()
          } catch (err) {
            if (
              err?.message?.includes('invalid_grant') ||
              err?.includes('invalid_grant') ||
              err?.error?.includes('invalid_grant')
            ) {
              if (localStorage && !localStorage.getItem('reloaded')) {
                localStorage.setItem('reloaded', 'true')
                window.location.reload()
              } else {
                localStorage.removeItem('reloaded')
                Sentry.captureException(err, { tags: { message: 'Error with refresh token' } })
                history.push('/logout', { message: UNEXPECTED_ERROR_MESSAGE })
              }
            }
          }
        } catch (error) {
          console.log('Unable to authorize user', error)
          Sentry.captureException(error)
          history.push('/logout', { message: UNEXPECTED_ERROR_MESSAGE })
        }
      }
    }

    init()
    // re-init client if session.user_id changes
  }, [session?.user_id, isPainterAccount, masqueradeCompanyId]) // eslint-disable-line

  // Listen for storage events across tabs, keep out masqueradeCompanyId in sync across tabs
  useEffect(() => {
    function handleStorage(event: StorageEvent) {
      if (event.key === MASQUERADE_COMPANY_ID_KEY) {
        // normalize the literal 'null' other tabs can write, so cacheScope doesn't become
        // `user_id:null` and mint a bogus IndexedDB store
        setMasqueradeCompanyId(event.newValue === 'null' ? null : event.newValue)
        window.localStorage.removeItem('lastPurge')
        history.push('/')
      }
    }

    window.addEventListener('storage', handleStorage)

    return () => {
      window.removeEventListener('storage', handleStorage)
    }
  }, [history])

  return (
    <Authorizer>
      {!client ? (
        <AppLoading withRecovery />
      ) : (
        // key by (user_id, masqueraded company) so the whole tree — including the providers holding
        // options/rates — remounts on a company switch. Keying on user_id alone meant masquerading
        // never remounted, leaving the previous company's settings in memory (ENG-1061).
        <ApolloProvider key={cacheScope} client={client}>
          <ApolloQueue>
            <LoadData session={session} masqueradeCompanyId={masqueradeCompanyId}>
              {children}
            </LoadData>
          </ApolloQueue>
        </ApolloProvider>
      )}
    </Authorizer>
  )
}

// fetch necessary data and put in appropriate providers
const LoadData = ({
  children,
  session,
  masqueradeCompanyId
}: {
  children?: React.ReactNode
  session: Auth0Session
  masqueradeCompanyId?: string | null
}) => {
  const history = useHistory()
  const apolloClient = useApolloClient()
  const { authenticate } = useAuth()
  const { online } = useConnection()
  const isPainterAccount = session?.app_metadata?.roles?.includes('painter')
  const roles = session?.app_metadata?.roles ?? []
  const isPrivileged = roles.includes('superadmin') || roles.includes('support') || roles.includes('corporate')

  const [launchDarklyReady, setLaunchDarklyReady] = useState(false)
  const [purging, setPurging] = useState(() => online && !isPainterAccount && checkNeedsPurge())

  function handleUnexpectedError() {
    history.push('/logout', { message: UNEXPECTED_ERROR_MESSAGE })
  }

  useEffect(() => {
    Sentry.setUser({
      username: session.name,
      email: session.email,
      id: session.user_id
    })

    const role = getHighestRole(session.app_metadata.roles)
    Sentry.setTags({
      role,
      company: session.app_metadata.company?.name,
      companyId: session.app_metadata.companyId
    })
  }, [session])

  useEffect(() => {
    if (purging) {
      purge()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purging])

  async function purge() {
    try {
      const appDocs = apolloClient.readQuery({
        query: AppDocsDocument
      })
      const offlineQuotes = apolloClient.readQuery<OfflineQuotesQuery>({
        query: OfflineQuotesDocument
      })

      await apolloClient.cache.reset()

      window.localStorage.setItem('lastPurge', JSON.stringify(Date.now()))

      // Re-seeding appDocs keeps offline users working across a purge, but it also carries whatever
      // was in the cache *through* the reset. A masquerade switch deletes `lastPurge` to force a
      // purge, so that re-seed is exactly how one company's options/rates survived into another
      // company's session and got saved over their documents (ENG-1061).
      //
      // Defence-in-depth on top of the per-company `idbName`: skip the re-seed only while
      // masquerading AND online, so the in-flight cache-and-network fetch is guaranteed to
      // repopulate appDocs for the correct company. If connectivity dropped after mount we still
      // re-seed rather than strand the user on the loading screen with nothing to render.
      if (!masqueradeCompanyId || !online) {
        apolloClient.writeQuery({
          query: AppDocsDocument,
          data: appDocs
        })
      }

      updateCache(offlineQuotes)
    } catch (error) {
      // if lastPurge is undefined, then this is expected because it's probably a brand new database
      if (JSON.parse(window.localStorage.getItem('lastPurge'))) {
        log.error('Unable to purge cache', { error: error?.error ?? error, sentry: true })
      }
    }
    setPurging(false)
  }

  /**
   * updates various cached queries using results of offlineQuotes
   */
  function updateCache(offlineQuotes: OfflineQuotesQuery) {
    if (offlineQuotes) {
      // put appDocs back into cache
      apolloClient.writeQuery({
        query: OfflineQuotesDocument,
        data: offlineQuotes
      })

      // get response of every offline quote and update the quote caches
      const { quotes = [], invoices = [], contacts = [], templates = [] } = offlineQuotes.offlineQuotes ?? {}

      const allQuotes = [...quotes, ...invoices, ...templates]
      allQuotes.forEach((quote) => {
        updateCachedQuote(apolloClient.cache as ApolloCache, quote)
      })

      contacts.forEach((contact) => {
        updateCachedContact(apolloClient.cache as ApolloCache, contact as any)
      })
    }
  }

  const appDocsQuery = useAppDocsQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
    onCompleted: async () => {
      const unsavedNewQuotes: QuoteDocument[] = []

      // we're online - try fetching offline quotes
      const offlineQuotesResponse = await apolloClient
        .query<OfflineQuotesQuery>({
          query: OfflineQuotesDocument,
          // network-only will still write to cache
          fetchPolicy: 'network-only'
        })
        .catch(() => null)

      if (offlineQuotesResponse) {
        updateCache(offlineQuotesResponse.data)

        const all = [
          ...(offlineQuotesResponse.data?.offlineQuotes?.quotes ?? []),
          ...(offlineQuotesResponse.data?.offlineQuotes?.invoices ?? [])
        ]

        // temporary check to make sure all new offline quotes made it
        // (this could be a false positive if 10 new offline quotes come in and push it beyond the limit)
        unsavedNewQuotes.forEach((quote) => {
          if (!all.find((q) => q._id === quote._id)) {
            console.error('Potentially lost offline quote', quote)
            analyticsError('Potentially lost offline quote', quote)
          }
        })
      }
    }
  })

  useEffect(() => {
    if (appDocsQuery.error && !appDocsQuery.data?.appDocs && !isPrivileged) {
      Sentry.captureException(appDocsQuery.error, { tags: { message: 'Unexpected error in AuthorizeUser' } })
      handleUnexpectedError()
    }
  }, [appDocsQuery.error])

  const appDocs = appDocsQuery?.data?.appDocs

  useEffect(() => {
    if (!appDocs) return
    const prefsTimestamp = (appDocs.preferences as any)?.lastPermissionsUpdated as string | undefined
    const jwtTimestamp = (session?.app_metadata as any)?.lastPermissionsUpdated as string | undefined
    if (prefsTimestamp && prefsTimestamp !== jwtTimestamp) {
      authenticate({ ignoreCache: true })
        .then(() => apolloClient.resetStore())
        .catch((err) => {
          console.error('Failed to refresh token after permissions update', err)
        })
    }
  }, [(appDocs?.preferences as any)?._rev])

  if ((!appDocs && !isPainterAccount) || purging) {
    return <AppLoading withRecovery />
  }

  const preferences = (appDocs?.preferences as any) ?? {}
  const companyDetails = appDocs?.companyDetails ?? session.app_metadata.company
  // use the prop rather than re-reading localStorage: another tab can change the stored value at any
  // moment, and reading it here could hand ClientOptionsProvider a different company than the one
  // this tree's Apollo cache was scoped to (ENG-1061)
  const companyId = masqueradeCompanyId || session.app_metadata.companyId

  const _stage = process.env.DEPLOYMENT_STAGE ?? 'local'
  const showDevTools =
    _stage === 'local' ||
    _stage === 'dev' ||
    (_stage === 'test' && window.localStorage.getItem('ps:devtools') === 'true')

  const innerContent = (
    <>
      {/*
        lang is intentionally hardcoded to 'en' because the internal app still has no per-user language preference.
        If that changes, swap this to a real preference value and move the provider lower only if runtime language
        changes need to be scoped to smaller translated subtrees.
      */}
      <TranslationProvider lang="en">
        <LaunchDarkly
          onReady={() => startTransition(() => setLaunchDarklyReady(true))}
          userDetails={{
            id: session.user_id,
            companyId,
            parentCompanyId: companyDetails?.parentCompanyId || null,
            email: session.email
          }}
        >
          <Theme>
            <SnackbarProvider>{launchDarklyReady || !online ? children : <AppLoading />}</SnackbarProvider>
          </Theme>
        </LaunchDarkly>
      </TranslationProvider>
    </>
  )

  const userProvider = (
    <UserProvider
      user={buildUserForProvider({ session, companyDetails })}
      preferences={preferences}
      remainingQuotes={appDocs?.remainingQuotes ?? 0}
      togglePaymentExpiryNoticeCache={() => {
        if (!appDocs) return
        apolloClient.cache.writeQuery<AppDocsQuery>({
          query: AppDocsDocument,
          data: {
            ...appDocsQuery.data,
            appDocs: {
              ...appDocs,
              companyDetails: { ...appDocs.companyDetails, paymentExpiryNotice: false }
            }
          }
        })
      }}
    >
      {showDevTools ? (
        <DevUserProvider onMockChange={() => apolloClient.resetStore().catch(() => {})}>{innerContent}</DevUserProvider>
      ) : (
        innerContent
      )}
    </UserProvider>
  )

  return (
    <ClientOptionsProvider clientId={companyId}>
      {showDevTools ? <DevPermissionsProvider>{userProvider}</DevPermissionsProvider> : userProvider}
    </ClientOptionsProvider>
  )
}

const checkNeedsPurge = () => {
  const lastPurge = JSON.parse(window.localStorage.getItem('lastPurge')) || 0
  const needsPurge = lastPurge < Date.now() - 1000 * 60 * 60 * 24 // once a day

  return needsPurge
}
